/**
 * useUserTeams Hook
 *
 * Fetches the Microsoft Teams that the authenticated user is a member of
 * in a managed tenant where they have a guest account.
 *
 * Uses an in-memory cache to prevent refetching when navigating between pages.
 */

import { useState, useEffect, useCallback } from 'react';
import { TenantApiService } from '../services/tenantApi';
import type { Team } from '../types/user';

interface UseUserTeamsOptions {
  tenantId: string;
  enabled?: boolean;
}

interface UseUserTeamsResult {
  teams: Team[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface CacheEntry {
  teams: Team[];
  timestamp: number;
}

// Module-level cache to persist across component mounts
const teamsCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch the user's joined Microsoft Teams in a managed tenant.
 *
 * @param options.tenantId - The managed tenant ID to query for teams
 * @param options.enabled - Whether to fetch teams (default: true)
 * @returns Object with teams, loading state, error, and refetch function
 */
export function useUserTeams({
  tenantId,
  enabled = true,
}: UseUserTeamsOptions): UseUserTeamsResult {
  // Initialize from cache if available
  const cachedEntry = tenantId ? teamsCache.get(tenantId) : undefined;
  const isCacheValid = cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_TTL_MS;

  const [teams, setTeams] = useState<Team[]>(isCacheValid ? cachedEntry.teams : []);
  const [isLoading, setIsLoading] = useState<boolean>(!isCacheValid && enabled && !!tenantId);
  const [error, setError] = useState<string | null>(null);

  const fetchTeams = useCallback(async (skipCache = false) => {
    // Don't fetch if disabled or no tenantId
    if (!enabled || !tenantId) {
      setTeams([]);
      setIsLoading(false);
      return;
    }

    // Check cache first (unless skipCache is true for manual refresh)
    if (!skipCache) {
      const cached = teamsCache.get(tenantId);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
        setTeams(cached.teams);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedTeams = await TenantApiService.getMyTeams(tenantId, skipCache);
      setTeams(fetchedTeams);
      // Update cache
      teamsCache.set(tenantId, { teams: fetchedTeams, timestamp: Date.now() });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch teams';
      setError(errorMessage);
      setTeams([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, enabled]);

  // Fetch teams on mount and when dependencies change
  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // Manual refetch bypasses cache
  const refetch = useCallback(async () => {
    await fetchTeams(true);
  }, [fetchTeams]);

  return {
    teams,
    isLoading,
    error,
    refetch,
  };
}
